// Saved work (agent artifacts) — pure helpers. Arc writes whatever JSON
// structure suits the piece of work, so there is no per-type schema to render
// against: the payload is walked into a flat list of display blocks (headings,
// text, labelled fields, lists, tables) that the viewer renders and the
// copy / download path serialises to Markdown.

const TYPE_LABELS = Object.freeze({
  case_summary: 'Case summary',
  entity_list: 'People & entities',
  timeline: 'Timeline',
  key_finding: 'Key finding',
  document_comparison: 'Document comparison',
  bookmark: 'Bookmark',
});

export function humanizeKey(key) {
  const s = String(key).replace(/[_-]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

export function savedWorkTypeLabel(type) {
  return TYPE_LABELS[type] || humanizeKey(type || 'Saved work');
}

const MAX_HEADING_LEVEL = 4;

function isScalar(v) {
  return v == null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

function isPlainObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function scalarText(v) {
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

// One table cell / list item for a value that is not worth its own section.
function inlineText(v) {
  if (isScalar(v)) return scalarText(v);
  if (Array.isArray(v)) return v.map(inlineText).filter(Boolean).join(', ');
  return Object.entries(v)
    .map(([k, val]) => {
      const t = inlineText(val);
      return t ? `${humanizeKey(k)}: ${t}` : '';
    })
    .filter(Boolean)
    .join('; ');
}

// update_artifact can replace a JSON payload with a string — sometimes that
// string is the JSON re-serialised. Recover the structure when it is.
function parseMaybeJson(text) {
  const t = text.trim();
  if (!(t.startsWith('{') || t.startsWith('['))) return undefined;
  try {
    const parsed = JSON.parse(t);
    return parsed != null && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function walk(value, level, out) {
  if (value == null || value === '') return;

  if (typeof value === 'string') {
    const parsed = parseMaybeJson(value);
    if (parsed !== undefined) walk(parsed, level, out);
    else out.push({ type: 'text', text: value });
    return;
  }
  if (isScalar(value)) {
    out.push({ type: 'text', text: scalarText(value) });
    return;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return;
    if (value.every(isPlainObject)) {
      const columns = [];
      value.forEach(row => Object.keys(row).forEach(k => { if (!columns.includes(k)) columns.push(k); }));
      out.push({
        type: 'table',
        columns: columns.map(humanizeKey),
        rows: value.map(row => columns.map(k => inlineText(row[k]))),
      });
      return;
    }
    out.push({ type: 'list', items: value.map(inlineText).filter(Boolean) });
    return;
  }

  // Plain object: scalar members become one labelled-fields block (kept in
  // order, flushed whenever a nested member interrupts them); nested members
  // get a heading and recurse.
  let fields = [];
  const flush = () => {
    if (fields.length) out.push({ type: 'fields', items: fields });
    fields = [];
  };
  Object.entries(value).forEach(([k, v]) => {
    if (v == null || v === '') return;
    if (isScalar(v) && !(typeof v === 'string' && parseMaybeJson(v) !== undefined)) {
      fields.push([humanizeKey(k), scalarText(v)]);
      return;
    }
    if (Array.isArray(v) && v.length === 0) return;
    flush();
    out.push({ type: 'heading', level: Math.min(level, MAX_HEADING_LEVEL), text: humanizeKey(k) });
    walk(v, level + 1, out);
  });
  flush();
}

/** Flatten an artifact payload into display blocks. */
export function savedWorkBlocks(content) {
  const out = [];
  walk(content, 2, out);
  return out;
}

const mdCell = (s) => String(s ?? '').replace(/\|/g, '\\|').replace(/\s*\n\s*/g, ' ');

/** Markdown for copy / download: title, optional sub-line, then the blocks. */
export function savedWorkMarkdown(blocks, { title, subtitle } = {}) {
  const parts = [];
  if (title) parts.push(`# ${title}`);
  if (subtitle) parts.push(`_${subtitle}_`);
  blocks.forEach(b => {
    if (b.type === 'heading') parts.push(`${'#'.repeat(b.level)} ${b.text}`);
    else if (b.type === 'text') parts.push(b.text);
    else if (b.type === 'fields') parts.push(b.items.map(([k, v]) => `**${k}:** ${v}`).join('  \n'));
    else if (b.type === 'list') parts.push(b.items.map(i => `- ${i}`).join('\n'));
    else if (b.type === 'table') {
      parts.push([
        `| ${b.columns.map(mdCell).join(' | ')} |`,
        `| ${b.columns.map(() => '---').join(' | ')} |`,
        ...b.rows.map(r => `| ${r.map(mdCell).join(' | ')} |`),
      ].join('\n'));
    }
  });
  return `${parts.join('\n\n')}\n`;
}
